import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateReminderDialog = React.memo(({ isOpen, onClose, lead, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    callback_date: '',
    callback_time: '',
    notes: '',
    priority: 'medium'
  });
  const [loading, setLoading] = useState(false);

  // REMOVED: Excessive logging causing re-renders

  // Pre-fill customer info when dialog opens with lead data
  useEffect(() => {
    if (isOpen && lead) {
      const customerName = `${lead.prenom || ''} ${lead.nom || ''}`.trim();
      const customerPhone = lead.numero_principal || lead.telephone || '';
      
      setFormData({
        customer_name: customerName,
        customer_phone: customerPhone,
        callback_date: '',
        callback_time: '',
        notes: '',
        priority: 'medium'
      });
      
      console.log('Form data updated!');
    } else if (!isOpen) {
      // Reset form when dialog closes
      setFormData({
        customer_name: '',
        customer_phone: '',
        callback_date: '',
        callback_time: '',
        notes: '',
        priority: 'medium'
      });
    }
  }, [isOpen, lead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // User enters date/time in Montreal local time
      // We need to convert it to UTC for the backend
      const localDateTimeStr = `${formData.callback_date}T${formData.callback_time}:00`;
      
      // Create a date object interpreting the input as Montreal time
      // Then convert to ISO string (which will be in UTC)
      const localDate = new Date(localDateTimeStr);
      
      // Get Montreal timezone offset at this specific date (handles DST)
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Montreal',
        year: 'numeric',
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      // The input is already in Montreal time, so we just need to add the timezone offset
      // Montreal is typically -05:00 (EST) or -04:00 (EDT)
      // Check if we're in DST for the selected date
      const testDate = new Date(formData.callback_date + 'T12:00:00');
      const jan = new Date(testDate.getFullYear(), 0, 1);
      const jul = new Date(testDate.getFullYear(), 6, 1);
      const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
      const isDST = testDate.getTimezoneOffset() < stdOffset;
      
      // Montreal offset: -05:00 in winter (EST), -04:00 in summer (EDT)
      const montrealOffset = isDST ? '-04:00' : '-05:00';
      
      // Create ISO string with Montreal timezone
      const callback_datetime = `${formData.callback_date}T${formData.callback_time}:00${montrealOffset}`;
      
      console.log('Submitting reminder with datetime (Montreal TZ):', callback_datetime);
      
      // Prepare reminder data
      const reminderData = {
        lead_id: lead?.id || lead?.row_number?.toString(),
        lead_source: lead?.source,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        callback_datetime: callback_datetime,
        notes: formData.notes,
        priority: formData.priority
      };

      const response = await fetch(`${BACKEND_URL}/api/reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reminderData)
      });

      // Clone response before reading body to avoid "body already used" errors
      const responseClone = response.clone();

      if (response.ok) {
        const result = await response.json();
        console.log('Reminder created:', result);
        alert('✅ Rappel créé avec succès!');
        
        // Reset form
        setFormData({
          customer_name: '',
          customer_phone: '',
          callback_date: '',
          callback_time: '',
          notes: '',
          priority: 'medium'
        });
        
        // Pass the created reminder to onSuccess for optimistic update
        if (onSuccess) onSuccess(result);
        onClose();
      } else {
        let errorMsg = 'Unknown error';
        try {
          const errorData = await responseClone.json();
          errorMsg = errorData.error || errorData.detail || JSON.stringify(errorData);
        } catch {
          errorMsg = await responseClone.text().catch(() => 'Failed to read error');
        }
        console.error('Failed to create reminder:', errorMsg);
        alert(`❌ Échec de la création du rappel: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error creating reminder:', error);
      alert('❌ Erreur lors de la création du rappel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>🔔 Créer un Rappel</DialogTitle>
          <DialogDescription>
            Planifier un rappel pour ce client potentiel
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer_name">Nom du client *</Label>
            <Input
              id="customer_name"
              value={formData.customer_name}
              onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              required
              placeholder="Prénom Nom"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_phone">Téléphone *</Label>
            <Input
              id="customer_phone"
              value={formData.customer_phone}
              onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
              required
              placeholder="819-123-4567"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callback_date">Date du rappel *</Label>
            <Input
              id="callback_date"
              type="date"
              value={formData.callback_date || ''}
              onChange={(e) => {
                console.log('Date changed:', e.target.value);
                setFormData({...formData, callback_date: e.target.value});
              }}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callback_time">Heure du rappel *</Label>
            <Input
              id="callback_time"
              type="time"
              value={formData.callback_time || ''}
              onChange={(e) => {
                console.log('Time changed:', e.target.value);
                setFormData({...formData, callback_time: e.target.value});
              }}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priorité</Label>
            <Select 
              value={formData.priority} 
              onValueChange={(value) => setFormData({...formData, priority: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">🔵 Faible</SelectItem>
                <SelectItem value="medium">🟡 Moyenne</SelectItem>
                <SelectItem value="high">🔴 Haute</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes / Raison</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Ex: Client intéressé par Honda Civic 2020, discuter financement..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Création...' : 'Créer Rappel'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

CreateReminderDialog.displayName = 'CreateReminderDialog';

export default CreateReminderDialog;
